// Coordinate system validation utilities

import { ContentRect, CoordinateSpaceUtils } from './CoordinateSpaces';

export interface ValidationContext {
  frontendRequest: {
    pageWidth: number;
    pageHeight: number;
    margins: { top: number; right: number; bottom: number; left: number };
    dpi: number;
  };
  backendPlan: {
    pageSizePx: { width: number; height: number };
    contentRectPx: ContentRect;
    dpi: number;
  };
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  diff?: ValidationDiff;
}

export interface ValidationDiff {
  timestamp: string;
  expected: {
    pageWidth: number;
    pageHeight: number;
    contentWidth: number;
    contentHeight: number;
    margins: { top: number; right: number; bottom: number; left: number };
    dpi: number;
  };
  actual: {
    pageWidth: number;
    pageHeight: number;
    contentWidth: number;
    contentHeight: number;
    contentRect: ContentRect;
    dpi: number;
  };
  deltas: {
    pageWidthDelta: number;
    pageHeightDelta: number;
    contentWidthDelta: number;
    contentHeightDelta: number;
    marginDeltas: {
      top: number;
      right: number;
      bottom: number;
      left: number;
    };
  };
}

export class CoordinateValidator {
  private static readonly EPSILON = 0.5; // 0.5px tolerance

  /**
   * Validate backend plan matches frontend expectations
   * Hard validation with fail-fast on mismatches
   */
  static validatePlan(context: ValidationContext): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    try {
      // Validate input context
      this.validateContext(context);
      
      // Calculate expected content dimensions
      const expectedContentWidth = context.frontendRequest.pageWidth - 
        context.frontendRequest.margins.left - context.frontendRequest.margins.right;
      const expectedContentHeight = context.frontendRequest.pageHeight - 
        context.frontendRequest.margins.top - context.frontendRequest.margins.bottom;

      // Hard validation: equality within epsilon
      const pageWidthDelta = Math.abs(context.backendPlan.pageSizePx.width - context.frontendRequest.pageWidth);
      if (pageWidthDelta > this.EPSILON) {
        errors.push(`Page width mismatch: expected ${context.frontendRequest.pageWidth}, got ${context.backendPlan.pageSizePx.width} (delta: ${pageWidthDelta.toFixed(2)}px)`);
      }

      const pageHeightDelta = Math.abs(context.backendPlan.pageSizePx.height - context.frontendRequest.pageHeight);
      if (pageHeightDelta > this.EPSILON) {
        errors.push(`Page height mismatch: expected ${context.frontendRequest.pageHeight}, got ${context.backendPlan.pageSizePx.height} (delta: ${pageHeightDelta.toFixed(2)}px)`);
      }

      const contentWidthDelta = Math.abs(context.backendPlan.contentRectPx.width - expectedContentWidth);
      if (contentWidthDelta > this.EPSILON) {
        errors.push(`Content width mismatch: expected ${expectedContentWidth}, got ${context.backendPlan.contentRectPx.width} (delta: ${contentWidthDelta.toFixed(2)}px)`);
      }

      const contentHeightDelta = Math.abs(context.backendPlan.contentRectPx.height - expectedContentHeight);
      if (contentHeightDelta > this.EPSILON) {
        errors.push(`Content height mismatch: expected ${expectedContentHeight}, got ${context.backendPlan.contentRectPx.height} (delta: ${contentHeightDelta.toFixed(2)}px)`);
      }

      // Validate content rectangle positioning
      const expectedContentX = context.frontendRequest.margins.left;
      const expectedContentY = context.frontendRequest.margins.top;
      
      const contentXDelta = Math.abs(context.backendPlan.contentRectPx.x - expectedContentX);
      if (contentXDelta > this.EPSILON) {
        errors.push(`Content X position mismatch: expected ${expectedContentX}, got ${context.backendPlan.contentRectPx.x} (delta: ${contentXDelta.toFixed(2)}px)`);
      }

      const contentYDelta = Math.abs(context.backendPlan.contentRectPx.y - expectedContentY);
      if (contentYDelta > this.EPSILON) {
        errors.push(`Content Y position mismatch: expected ${expectedContentY}, got ${context.backendPlan.contentRectPx.y} (delta: ${contentYDelta.toFixed(2)}px)`);
      }

      // DPI validation
      if (context.backendPlan.dpi && context.frontendRequest.dpi) {
        const dpiDelta = Math.abs(context.backendPlan.dpi - context.frontendRequest.dpi);
        if (dpiDelta > 0.1) {
          warnings.push(`DPI mismatch: expected ${context.frontendRequest.dpi}, got ${context.backendPlan.dpi} (delta: ${dpiDelta.toFixed(1)})`);
        }
      }

      const isValid = errors.length === 0;

      if (!isValid) {
        const diff = this.generateStructuredDiff(context, {
          pageWidthDelta,
          pageHeightDelta,
          contentWidthDelta,
          contentHeightDelta,
          expectedContentWidth,
          expectedContentHeight,
          expectedContentX,
          expectedContentY
        });

        return { isValid: false, errors, warnings, diff };
      }

      return { isValid: true, errors: [], warnings };

    } catch (error) {
      errors.push(`Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return { isValid: false, errors, warnings };
    }
  }

  /**
   * Validate the validation context itself
   */
  private static validateContext(context: ValidationContext): void {
    if (!context.frontendRequest) {
      throw new Error('Frontend request is required');
    }

    if (!context.backendPlan) {
      throw new Error('Backend plan is required');
    }

    // Validate frontend request
    const req = context.frontendRequest;
    if (typeof req.pageWidth !== 'number' || req.pageWidth <= 0) {
      throw new Error('Frontend request must have valid pageWidth');
    }

    if (typeof req.pageHeight !== 'number' || req.pageHeight <= 0) {
      throw new Error('Frontend request must have valid pageHeight');
    }

    if (!req.margins || typeof req.margins !== 'object') {
      throw new Error('Frontend request must have margins object');
    }

    // Validate backend plan
    const plan = context.backendPlan;
    if (!plan.pageSizePx || typeof plan.pageSizePx.width !== 'number' || typeof plan.pageSizePx.height !== 'number') {
      throw new Error('Backend plan must have valid pageSizePx');
    }

    if (!plan.contentRectPx) {
      throw new Error('Backend plan must have contentRectPx');
    }

    CoordinateSpaceUtils.validateContentRect(plan.contentRectPx);
  }

  /**
   * Generate structured diff for debugging
   */
  private static generateStructuredDiff(
    context: ValidationContext,
    deltas: {
      pageWidthDelta: number;
      pageHeightDelta: number;
      contentWidthDelta: number;
      contentHeightDelta: number;
      expectedContentWidth: number;
      expectedContentHeight: number;
      expectedContentX: number;
      expectedContentY: number;
    }
  ): ValidationDiff {
    return {
      timestamp: new Date().toISOString(),
      expected: {
        pageWidth: context.frontendRequest.pageWidth,
        pageHeight: context.frontendRequest.pageHeight,
        contentWidth: deltas.expectedContentWidth,
        contentHeight: deltas.expectedContentHeight,
        margins: context.frontendRequest.margins,
        dpi: context.frontendRequest.dpi
      },
      actual: {
        pageWidth: context.backendPlan.pageSizePx.width,
        pageHeight: context.backendPlan.pageSizePx.height,
        contentWidth: context.backendPlan.contentRectPx.width,
        contentHeight: context.backendPlan.contentRectPx.height,
        contentRect: context.backendPlan.contentRectPx,
        dpi: context.backendPlan.dpi
      },
      deltas: {
        pageWidthDelta: deltas.pageWidthDelta,
        pageHeightDelta: deltas.pageHeightDelta,
        contentWidthDelta: deltas.contentWidthDelta,
        contentHeightDelta: deltas.contentHeightDelta,
        marginDeltas: {
          top: Math.abs(context.backendPlan.contentRectPx.y - deltas.expectedContentY),
          right: Math.abs((context.backendPlan.pageSizePx.width - context.backendPlan.contentRectPx.x - context.backendPlan.contentRectPx.width) - context.frontendRequest.margins.right),
          bottom: Math.abs((context.backendPlan.pageSizePx.height - context.backendPlan.contentRectPx.y - context.backendPlan.contentRectPx.height) - context.frontendRequest.margins.bottom),
          left: Math.abs(context.backendPlan.contentRectPx.x - deltas.expectedContentX)
        }
      }
    };
  }

  /**
   * Log structured validation failure
   */
  static logValidationFailure(validation: ValidationResult): void {
    console.error('🚨 [CoordinateValidator] Backend plan validation failed:', {
      errors: validation.errors,
      warnings: validation.warnings,
      diff: validation.diff,
      timestamp: new Date().toISOString(),
      action: 'FALLING_BACK_TO_LOCAL_RENDER'
    });
  }

  /**
   * Log successful validation
   */
  static logValidationSuccess(validation: ValidationResult): void {
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ [CoordinateValidator] Backend plan validation passed:', {
        warnings: validation.warnings,
        timestamp: new Date().toISOString()
      });
    }
  }

  /**
   * Quick validation for common cases
   */
  static quickValidate(
    frontendPageWidth: number,
    frontendPageHeight: number,
    backendPageWidth: number,
    backendPageHeight: number
  ): boolean {
    const widthDelta = Math.abs(backendPageWidth - frontendPageWidth);
    const heightDelta = Math.abs(backendPageHeight - frontendPageHeight);
    
    return widthDelta <= this.EPSILON && heightDelta <= this.EPSILON;
  }
}
